import React, { useState, useEffect } from 'react';

import { CAMERA_ROTATION_OPTIONS } from '@vlabs/api-bindings/src/constants';
import { RegionsEditModal } from '@vlabs/monokit/pages/regions-edit-modal';
import { useHlsWithRetry } from '@vlabs/monokit/shared/lib/video/useHlsWithRetry';
import { CropIcon } from '@vlabs/icons';
import { Control, useWindowSize } from '@vlabs/uikit';
import cn from 'classnames';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import st from './RegionsEdit.module.sass';

const EMPTY_DROI = { mode: 'abs', form: 'wkt', area: '' };

export function RegionsEdit() {
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const { setValue } = useFormContext();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [shouldOpenModal, setShouldOpenModal] = useState(false);
  const [error, setError] = useState(false);

  const {
    analytic,
    initialRotationDeg,
    isVideoStream,
  } = useWatch({
    name: ['analytics', 'data'],
    compute: ([analytics, data]) => {
      return {
        analytic: analytics?.[0],
        initialRotationDeg: data?.rotation?.value,
        isVideoStream: data?.type?.value === 'stream',
      };
    },
  });

  const { streamId } = useParams();
  const { status, retransmission } = useHlsWithRetry(shouldOpenModal && streamId);

  const resetModalState = () => {
    setIsModalVisible(false);
    setShouldOpenModal(false);
  };

  useEffect(() => {
    if (status === 'loaded') setIsModalVisible(true);
    if (status === 'failed') {
      resetModalState();
      setError(true);
    }
  }, [status]);

  const onClick = () => {
    setError(false);
    setShouldOpenModal(true);
  };

  const handleEditData = ({ roi, droi, rotationDeg }) => {
    setValue('analytics.0.parameters.parameters.roi', roi);
    setValue('analytics.0.parameters.parameters.droi', droi || EMPTY_DROI);
    setValue('data.rotation', CAMERA_ROTATION_OPTIONS[rotationDeg]);

    resetModalState();
  };

  return (
    <div className={st.Root}>
      <h6>{t('videoAnalytics:editAndCreateStreamsPageTitles.frame_and_zones_settings')}</h6>

      <Control.Button
        className={cn(st.SetRegionsButton, {
          [st.SetRegionsButton_large]: windowSize.width >= 1920,
        })}
        data-testid="queue.openRegionsEditModalButton"
        disabled={!isVideoStream}
        fullWidth
        icon={<CropIcon />}
        onClick={onClick}
        variant="dimmed"
      >
        {t('кнопка.настроить')}
      </Control.Button>

      {!!error && (
        <div className={st.Error}>
          <span className={st.Error__Title}>{t('videoAnalytics:error.failed_load_retransmission.title')}</span>
          <span>{t('videoAnalytics:error.failed_load_retransmission.message')}</span>
        </div>
      )}

      <RegionsEditModal
        droi={analytic?.parameters?.parameters?.droi}
        handleEditData={handleEditData}
        initialRotationDeg={initialRotationDeg}
        isOpen={isModalVisible}
        key={isModalVisible}
        onClose={resetModalState}
        retransmission={retransmission}
        roi={analytic?.parameters?.parameters?.roi}
      />
    </div>
  );
}
